import type { Locale } from "~/core/i18n";
import { pick } from "~/core/i18n/pick";

/**
 * Every figure and moment the interface prints, in the reader's language.
 *
 * Both modules read from here. The Agent carried its own set — English month
 * arrays, `date-fns` patterns, a formatter per feature — which printed "Jul 31"
 * on a Chinese screen and disagreed with the panel about what a date looks
 * like. What still differs between the two is the time zone, and that is the
 * caller's to resolve and pass in: this module formats a moment in a zone, it
 * does not decide which zone a person is in.
 */

function tag(locale: Locale): string {
	return pick(locale, ["en-US", "zh-CN"]);
}

/** An ISO string or a `Date`; anything unparseable reads as absent. */
type Moment = string | Date | null | undefined;

function parse(value: Moment): Date | null {
	if (!value) return null;
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** A country or region by its ISO code, as the reader's language names it: `JP` is Japan, or 日本. */
export function region(locale: Locale, code: string): string {
	return new Intl.DisplayNames([tag(locale)], { type: "region" }).of(code) ?? code;
}

/** A language by its ISO 639 code, as the reader's language names it: `ja` is Japanese, or 日语. */
export function language(locale: Locale, code: string): string {
	return new Intl.DisplayNames([tag(locale)], { type: "language" }).of(code) ?? code;
}

export function number(locale: Locale, value: number): string {
	return new Intl.NumberFormat(tag(locale)).format(value);
}

export function percent(locale: Locale, value: number, digits = 0): string {
	return new Intl.NumberFormat(tag(locale), {
		style: "percent",
		maximumFractionDigits: digits,
	}).format(value / 100);
}

/**
 * The account's balance, as the sidebar's account row and the Agent's chip both
 * print it. Absent rather than zero while there is no account to read it from:
 * a zero is a balance.
 */
export function balance(locale: Locale, credits: number | null | undefined): string | null {
	return typeof credits === "number" ? number(locale, credits) : null;
}

/** USD micros to a readable amount. */
export function usd(locale: Locale, micros: number): string {
	return new Intl.NumberFormat(tag(locale), {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 2,
	}).format(micros / 1_000_000);
}

/** A day. `timeZone` names whose calendar it is read on — `UTC` for a value stored as a UTC midnight. */
export function date(locale: Locale, value: Moment, timeZone?: string): string {
	const moment = parse(value);
	if (!moment) return "—";
	return new Intl.DateTimeFormat(tag(locale), {
		year: "numeric",
		month: "short",
		day: "numeric",
		timeZone,
	}).format(moment);
}

/**
 * A moment, not a day: for a schedule's next run or a plan's due time. Given a
 * zone, the zone is printed too, because a time read in somebody else's zone
 * without saying so is the wrong time.
 */
export function dateTime(locale: Locale, value: Moment, timeZone?: string): string {
	const moment = parse(value);
	if (!moment) return "—";
	const options: Intl.DateTimeFormatOptions = timeZone
		? {
				year: "numeric",
				month: "short",
				day: "numeric",
				hour: "numeric",
				minute: "2-digit",
				timeZone,
				timeZoneName: "short",
			}
		: { dateStyle: "medium", timeStyle: "short" };
	return new Intl.DateTimeFormat(tag(locale), options).format(moment);
}

/** A day in a column too narrow for a year: `Jul 31`, `7月31日`. */
export function monthDay(locale: Locale, value: Moment): string {
	const moment = parse(value);
	if (!moment) return "—";
	return new Intl.DateTimeFormat(tag(locale), { month: "short", day: "numeric" }).format(moment);
}

/** A moment in a row too dense for a year: `Jul 31, 2:05 PM`, `7月31日 14:05`. */
export function shortDateTime(locale: Locale, value: Moment, timeZone?: string): string {
	const moment = parse(value);
	if (!moment) return "—";
	return new Intl.DateTimeFormat(tag(locale), {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		timeZone,
	}).format(moment);
}

/**
 * A calendar date as it was written — `2026-07-31`, or `2026-07-31T14:22` with
 * its time — never moved into a zone. A range somebody picked on a date control
 * is a pair of days, not a pair of instants, and reading it as UTC midnight
 * printed the day before for everybody west of Greenwich.
 */
export function shortDate(locale: Locale, value: string): string {
	const [day, time] = value.split("T");
	const [y, m, d] = day.split("-").map(Number);
	const [hh = 0, mm = 0] = time ? time.slice(0, 5).split(":").map(Number) : [];
	const written = new Date(Date.UTC(y, m - 1, d, hh, mm));
	if (Number.isNaN(written.getTime())) return value;
	return new Intl.DateTimeFormat(tag(locale), {
		month: "short",
		day: "numeric",
		...(time ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {}),
		timeZone: "UTC",
	}).format(written);
}

export function relative(locale: Locale, value: Moment): string | null {
	const moment = parse(value);
	if (!moment) return null;

	const seconds = Math.round((moment.getTime() - Date.now()) / 1000);
	const rtf = new Intl.RelativeTimeFormat(tag(locale), { numeric: "auto" });
	const units: [Intl.RelativeTimeFormatUnit, number][] = [
		["year", 31_536_000],
		["month", 2_592_000],
		["week", 604_800],
		["day", 86_400],
		["hour", 3_600],
		["minute", 60],
	];
	for (const [unit, size] of units) {
		if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
	}
	return rtf.format(seconds, "second");
}

/**
 * How long a run took: `0.44ms`, `850ms`, `5.1s`, `2m 3s`, `1h 4m`.
 *
 * No locale, on purpose. These are the unit symbols a trace, a terminal and a
 * log line all print in either language, and a column of them has to line up
 * with the numbers the engine itself logged. A string that is already a
 * duration (`"500ms"`) is read for its number; one with no number in it is
 * returned as it came. `null` when there is nothing to format, so the caller
 * picks its own dash.
 */
export function duration(value: number | string | null | undefined, precision = 0): string | null {
	if (value === undefined || value === null) return null;

	let ms: number;
	if (typeof value === "string") {
		ms = Number.parseFloat(value.replace(/[^0-9.-]/g, ""));
		if (!Number.isFinite(ms)) return value;
	} else {
		ms = value;
		// A block cancelled before it ended has no end time to subtract.
		if (!Number.isFinite(ms)) return "—";
	}

	if (ms < 1) return ms < 0.005 ? "0ms" : `${ms.toFixed(2)}ms`;
	if (ms < 1000) return `${Math.round(ms)}ms`;

	const seconds = ms / 1000;
	if (seconds < 60) {
		return precision > 0 ? `${seconds.toFixed(precision).replace(/\.?0+$/, "")}s` : `${Math.floor(seconds)}s`;
	}

	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ${Math.floor(seconds % 60)}s`;
	return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** A signed delta, always with its sign, for a movement figure. */
export function signed(locale: Locale, value: number, digits = 0): string {
	return new Intl.NumberFormat(tag(locale), {
		maximumFractionDigits: digits,
		signDisplay: "exceptZero",
	}).format(value);
}
